import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Send, Sparkles, ShieldCheck, ArrowUpRight, Package } from "lucide-react";

/**
 * Lob-powered "Auto-mail via Aura" dialog.
 * Consumes 1 monthly quota (Plus: unlimited, Basic: 5/mo included) or 1 mail credit.
 */
export function AutoMailDialog({ open, onOpenChange, disputeId, onSent }) {
  const { user } = useAuth();
  const paid = user?.plan === "basic" || user?.plan === "plus";

  const [quota, setQuota] = useState(null);
  const [loadingQuota, setLoadingQuota] = useState(false);
  const [sending, setSending] = useState(false);
  const [addr, setAddr] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("aura_mail_addr") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!open || !paid) return;
    setLoadingQuota(true);
    api
      .get("/mail/quota")
      .then(({ data }) => setQuota(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoadingQuota(false));
  }, [open, paid]);

  const sendNow = async () => {
    if (!addr.consumer_address_line1 || !addr.consumer_city || !addr.consumer_state || !addr.consumer_zip) {
      toast.error("Please fill in your mailing address");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/mail/send", {
        dispute_id: disputeId,
        consumer_address_line1: addr.consumer_address_line1,
        consumer_address_line2: addr.consumer_address_line2 || null,
        consumer_city: addr.consumer_city,
        consumer_state: addr.consumer_state,
        consumer_zip: addr.consumer_zip,
        origin_url: window.location.origin,
      });
      try {
        localStorage.setItem("aura_mail_addr", JSON.stringify(addr));
      } catch (e) {
        console.warn("localStorage unavailable", e);
      }
      // Backend now returns a Stripe checkout URL. $12 charged before Lob mails the letter.
      if (data?.checkout_url) {
        toast.success("Redirecting to secure checkout — $12 per letter");
        window.location.href = data.checkout_url;
        return;
      }
      toast.success("Certified letter queued at USPS");
      onOpenChange(false);
      if (onSent) onSent(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const buyPack = async () => {
    try {
      const { data } = await api.post("/mail/buy-credits", {
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const priceStr = quota?.price_per_letter_cents
    ? `$${(quota.price_per_letter_cents / 100).toFixed(2).replace(/\.00$/, "")}`
    : "$12";
  const canSend = paid && quota;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-[#131518] border-[#26282C] text-[#F5F3EE]"
        data-testid="auto-mail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">
            Send via <span className="text-[#D5FF3F]">USPS Certified Mail</span>
          </DialogTitle>
          <DialogDescription className="text-[#A8ADA5]">
            Aura prints, folds, stamps, and mails your letter as USPS Certified Mail with Return
            Receipt. You get the tracking number + delivery confirmation instantly.
          </DialogDescription>
        </DialogHeader>

        {!paid ? (
          <div className="py-6">
            <div className="card-surface p-5 mb-4">
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Certified mail is a paid feature.</div>
                  <div className="text-sm text-[#A8ADA5] mt-1">
                    Unlock with Basic or Plus. USPS Certified Mail is $12 per letter (real postage +
                    Return Receipt via Lob).
                  </div>
                </div>
              </div>
            </div>
            <Link to="/app/pricing" onClick={() => onOpenChange(false)}>
              <Button className="w-full rounded-full btn-lime h-11" data-testid="auto-mail-upgrade-btn">
                See packages <ArrowUpRight size={14} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        ) : quota?.lob_configured === false ? (
          <div className="py-4 text-sm text-[#FF6B4A] font-mono">
            The certified-mail service isn&apos;t set up yet. Ask your admin to add a Lob API key.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Per-letter cost — charged before the letter goes out */}
            <div className="card-surface p-3 flex items-center justify-between text-sm">
              <div>
                <div className="overline mb-0.5">Cost</div>
                <div className="font-mono text-[#F5F3EE]" data-testid="auto-mail-price">
                  {loadingQuota ? "…" : `${priceStr} · USPS Certified Mail w/ Return Receipt`}
                </div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#D5FF3F] text-[#0B0C0E]">
                Real tracking
              </span>
            </div>

            {/* Address */}
            <div>
              <div className="overline mb-2">Your return address</div>
              <Input
                required
                value={addr.consumer_address_line1 || ""}
                onChange={(e) => setAddr({ ...addr, consumer_address_line1: e.target.value })}
                placeholder="Street address"
                data-testid="auto-mail-line1"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
              <Input
                value={addr.consumer_address_line2 || ""}
                onChange={(e) => setAddr({ ...addr, consumer_address_line2: e.target.value })}
                placeholder="Apt/Suite (optional)"
                data-testid="auto-mail-line2"
                className="mt-2 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
              <div className="grid grid-cols-6 gap-2 mt-2">
                <div className="col-span-3">
                  <Label className="overline">City</Label>
                  <Input
                    required
                    value={addr.consumer_city || ""}
                    onChange={(e) => setAddr({ ...addr, consumer_city: e.target.value })}
                    data-testid="auto-mail-city"
                    className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
                  />
                </div>
                <div className="col-span-1">
                  <Label className="overline">St</Label>
                  <Input
                    required
                    maxLength={2}
                    value={addr.consumer_state || ""}
                    onChange={(e) =>
                      setAddr({ ...addr, consumer_state: e.target.value.toUpperCase() })
                    }
                    data-testid="auto-mail-state"
                    className="mt-1 rounded-sm font-mono uppercase bg-[#0B0C0E] border-[#26282C]"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="overline">ZIP</Label>
                  <Input
                    required
                    value={addr.consumer_zip || ""}
                    onChange={(e) => setAddr({ ...addr, consumer_zip: e.target.value })}
                    data-testid="auto-mail-zip"
                    className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-sm border border-[#D5FF3F]/20 bg-[#D5FF3F]/5 p-3 flex items-start gap-2 text-xs text-[#A8ADA5] font-mono">
              <ShieldCheck size={13} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
              <span>
                USPS Certified Mail with Return Receipt. Tracking number + delivery date logged
                automatically. Legal proof for FCRA §1681i disputes.
              </span>
            </div>

            <DialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-full btn-outline"
              >
                Cancel
              </Button>
              <Button
                onClick={sendNow}
                disabled={sending || !canSend}
                data-testid="auto-mail-send-btn"
                className="rounded-full btn-lime h-11 px-6"
              >
                {sending ? (
                  <>
                    <Sparkles size={15} className="mr-2 animate-pulse" /> Redirecting…
                  </>
                ) : (
                  <>
                    <Send size={14} className="mr-2" /> Pay {priceStr} &amp; send
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
